import React, { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Tooltip } from "@mui/material";
import { Download, Edit, Mail } from "lucide-react";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import EmptyState from "assets/images/medications-empty-state.svg";
import { AddPrescriptionForm } from "components/AddPrescriptionForm/AddPrescriptionForm";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { formatStatusLabel } from "features/documentTypes/tables/DocumentTypesTable/utils/formatStatusLabel";
import { summarisePrescribedMedications } from "features/medications/utils/formatPrescribedMedication";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";

import { SendPrescriptionForm } from "./SendPrescriptionForm";
import style from "./Table.module.css";

const ROWS_OPTIONS = [10, 20, 50, 100];

export const Table = ({
  items,
  isLoading,
  isError,
  onRetry,
  withoutEditPrescription,
  currentPage,
  setCurrentPage,
  searchQuery,
  setSearchQuery,
  totalRecords,
  rows = 10,
  setRows,
}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const patientId = useCurrentPatientId();

  const [sendItem, setSendItem] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const handleDownload = useCallback(
    async (item) => {
      try {
        const axiosInstance = await createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}/patientprescription/download/prescription/${item.id}/patient/${patientId}`,
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `prescription_${item.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        toast.error("Error downloading prescription");
      }
    },
    [tenantVal, patientId]
  );

  const columns = useMemo(
    () => [
      {
        id: "datePrescribed",
        header: "Prescribed",
        width: "130px",
        render: (item) => item.datePrescribed || "-",
      },
      /*
       * What was actually sent (KIN-1966). Admin answer "has my prescription been sent?" from
       * this list, and the follow-up is always "what, and for how long" — a doctor sending one
       * month instead of six is the thing to spot, and it used to take opening every row.
       *
       * The first two are shown with a count for the rest rather than letting a prescription
       * with eight medications set the height of every row in the table.
       */
      {
        id: "medications",
        header: "Medications",
        /*
         * minmax(0, 2fr), not minmax(220px, …): a floor here is what pushed the row actions off
         * screen (KIN-1983). The column takes a share of the row and its text truncates; the
         * full list is on the tooltip.
         */
        width: "minmax(0, 2fr)",
        render: (item) => {
          const medications = summarisePrescribedMedications(item);

          if (!medications.length) return "-";

          const shown = medications.slice(0, 2);
          const remaining = medications.length - shown.length;

          return (
            /*
             * The tooltip is wired here rather than through the column's getTooltipText, which
             * DataTableCell only honours once the content is horizontally clipped — a stacked
             * block never is, so that tooltip could not appear however wide the medication name.
             */
            <Tooltip
              title={
                <span className={style.medicationsTooltip}>
                  {medications.map((medication) => (
                    <span key={medication.key}>
                      {[medication.name, medication.detail]
                        .filter(Boolean)
                        .join(" — ")}
                    </span>
                  ))}
                </span>
              }
              placement="top"
              enterDelay={200}
            >
              <span className={style.medicationsCell}>
                {shown.map((medication) => (
                  <span key={medication.key} className={style.medication}>
                    <span className={style.medicationName}>{medication.name}</span>
                    {!!medication.detail && (
                      <span className={style.medicationDetail}>
                        {medication.detail}
                      </span>
                    )}
                  </span>
                ))}
                {remaining > 0 && (
                  <span className={style.medicationMore}>
                    +{remaining} more — hover to see all
                  </span>
                )}
              </span>
            </Tooltip>
          );
        },
      },
      {
        id: "doctor",
        header: "Doctor",
        width: "minmax(0, 1fr)",
        render: (item) =>
          item.prescribedStaffMember?.firstName &&
          item.prescribedStaffMember?.lastName
            ? `${item.prescribedStaffMember.firstName} ${item.prescribedStaffMember.lastName}`
            : "N/A",
      },
      {
        id: "pharmacyName",
        header: "Pharmacy",
        width: "minmax(0, 1fr)",
        render: (item) => item.pharmacyName || "N/A",
      },
      {
        id: "status",
        header: "Status",
        width: "minmax(150px, max-content)",
        render: (item) => {
          const c = item.prescriptionSendStatus === "SENT" ? PILL_COLORS.statusSent : PILL_COLORS.statusNoStatus;
          const sentOn = item.pharmacySentDate ? moment(item.pharmacySentDate).format("DD-MM-YYYY") : null;

          return (
            <div className={style.statusCell}>
              <Pill color={c.color} bg={c.bg} border={c.border}>{formatStatusLabel(item.prescriptionSendStatus)}</Pill>
              {!!sentOn && <span className={style.sentDate}>{sentOn}</span>}
            </div>
          );
        },
      },
    ],
    []
  );

  const rowActions = useMemo(() => {
    const actions = [
      { label: "Send", icon: Mail, primary: true, onClick: (item) => setSendItem(item) },
      { label: "Download", icon: Download, primary: true, onClick: handleDownload },
    ];
    if (!withoutEditPrescription) {
      actions.push({
        label: "Edit",
        icon: Edit,
        primary: true,
        onClick: (item) => setEditItem(item),
      });
    }
    return actions;
  }, [withoutEditPrescription, handleDownload]);

  return (
    <>
      <DataTable
        columns={columns}
        data={items}
        /*
         * Without this the table sizes to max-content, so every fr share resolves against the
         * widest medication name and the Download/Edit actions end up past the right edge behind
         * a horizontal scrollbar (KIN-1983).
         */
        noScroll

        getRowId={(row) => row.id}
        isLoading={isLoading}
        isError={isError}
        errorLabel="this patient's prescriptions"
        onRetry={onRetry}
        emptyIllustration={searchQuery ? undefined : EmptyState}
        emptyTitle={searchQuery ? "No prescriptions found" : "No prescriptions"}
        emptyText={
          searchQuery
            ? "Try adjusting your search or filters to find what you're looking for."
            : "Once the patient receives a prescription, it will appear here."
        }
        search={{
          value: searchQuery,
          onChange: (v) => {
            setSearchQuery(v);
            setCurrentPage(1);
          },
          placeholder: "Search prescriptions...",
        }}
        rowActions={rowActions}
        currentPage={currentPage}
        rowsPerPage={rows}
        rowsValue={rows}
        onRowsChange={setRows ? (val) => { setRows(val); setCurrentPage(1); } : undefined}
        rowsOptions={ROWS_OPTIONS}
        totalCount={totalRecords}
        onPageChange={setCurrentPage}
      />

      {sendItem && (
        <Dialog
          open
          title="Send to pharmacy"
          onClose={() => setSendItem(null)}
          width={560}
        >
          <SendPrescriptionForm
            onSuccess={() => setSendItem(null)}
            onSkip={() => setSendItem(null)}
            prescriptionId={sendItem.id}
            patientId={patientId}
          />
        </Dialog>
      )}

      {editItem && (
        <Dialog
          open
          title="Edit Prescription"
          onClose={() => setEditItem(null)}
          width={1100}
        >
          <AddPrescriptionForm
            initialData={{
              id: editItem.id,
              filePath: editItem.filePath,
              instructions: editItem.note || "",
              medicines: editItem.medications.map((medication) => ({
                id: medication.id,
                dosage: medication.dosage || "",
                form: medication.form || "",
                startDate: medication.startDate ? moment(medication.startDate).format("DD-MM-YYYY") : "",
                finishDate: medication.finishDate ? moment(medication.finishDate).format("DD-MM-YYYY") : "",
                duration: medication.duration != null ? medication.duration.toString() : "",
                durationFormat: medication.durationFormat || "",
                usageType: medication.acuteLongTerm || "",
                repeat: medication.repeat != null ? medication.repeat.toString() : "",
                quantity: medication.quantity != null ? medication.quantity.toString() : "",
                tradeName: medication.tradeName || "",
                manufacturer: medication.manufacturer || "",
                packSize: medication.pack != null ? medication.pack.toString() : "",
                medicineId: medication.medicationId || "",
                route: medication.route || "",
                frequency: medication.frequency || "",
                isManual: !medication.medicationId,
              })),
            }}
            onSuccess={() => setEditItem(null)}
            onCancel={() => setEditItem(null)}
          />
        </Dialog>
      )}
    </>
  );
};
